"use client"

import { useAuth } from "@clerk/nextjs"
import { useCallback, useEffect, useRef, useState } from "react"

import { syncTimelineLocalRsvpFromServer } from "@/features/rsvp/sync-timeline-local-rsvp"
import type { PersistedRsvpStatus } from "@/lib/events/types"
import {
  fetchEventRsvpSnapshot,
  postEventRsvp,
  type EventRsvpSnapshot,
  type RsvpWireUserStatus,
} from "@/lib/rsvp-api"

export type UseRsvpForEventOptions = {
  occurrenceId?: string | null
  /** From GET /api/events/:id — drives display until snapshot fetch completes. */
  serverMine?: PersistedRsvpStatus | null | undefined
  /** After mutation success (before timeline sync). */
  onInvalidate?: () => void | Promise<void>
}

function isAbortError(e: unknown): boolean {
  return e instanceof DOMException && e.name === "AbortError"
}

function mineToWire(m: PersistedRsvpStatus | null | undefined): RsvpWireUserStatus {
  if (m === "going" || m === "maybe" || m === "declined") return m
  return "no_response"
}

/**
 * Single RSVP surface for one event: counts + wire user status + optimistic `setStatus`.
 */
export function useRsvpForEvent(
  eventId: string | null | undefined,
  options?: UseRsvpForEventOptions,
) {
  const { userId } = useAuth()
  const oid = options?.occurrenceId ?? null
  const abortRef = useRef<AbortController | null>(null)
  const genRef = useRef(0)

  const serverBase = mineToWire(options?.serverMine)
  const [counts, setCounts] = useState<EventRsvpSnapshot["counts"]>({
    going: 0,
    maybe: 0,
    declined: 0,
  })
  const [optimistic, setOptimistic] = useState<RsvpWireUserStatus | null>(null)

  useEffect(() => {
    setOptimistic(null)
  }, [eventId])

  useEffect(() => {
    if (!optimistic) return
    if (optimistic === serverBase) setOptimistic(null)
  }, [optimistic, serverBase])

  const userStatus = optimistic ?? serverBase

  const refreshSnapshot = useCallback(async () => {
    const id = eventId?.trim()
    if (!userId?.trim() || !id) return
    abortRef.current?.abort()
    const ac = new AbortController()
    abortRef.current = ac
    try {
      const snap = await fetchEventRsvpSnapshot(id, oid, { signal: ac.signal })
      setCounts(snap.counts)
    } catch (e) {
      if (!isAbortError(e)) {
        /* keep prior counts */
      }
    }
  }, [eventId, userId, oid])

  useEffect(() => {
    void refreshSnapshot()
  }, [refreshSnapshot])

  const [pending, setPending] = useState(false)

  const setStatus = useCallback(
    async (next: PersistedRsvpStatus | "no_response"): Promise<EventRsvpSnapshot | null> => {
      const id = eventId?.trim()
      if (!userId?.trim() || !id) return null

      abortRef.current?.abort()
      const ac = new AbortController()
      abortRef.current = ac
      const gen = genRef.current + 1
      genRef.current = gen

      setOptimistic(next)
      setPending(true)
      try {
        const snap = await postEventRsvp(id, next, oid, { signal: ac.signal })
        if (genRef.current !== gen) return null
        setCounts(snap.counts)
        syncTimelineLocalRsvpFromServer(id, snap.userStatus)
        await options?.onInvalidate?.()
        setOptimistic(null)
        return snap
      } catch (e) {
        if (!isAbortError(e) && genRef.current === gen) {
          setOptimistic(null)
        }
        return null
      } finally {
        if (genRef.current === gen) setPending(false)
      }
    },
    [eventId, userId, oid, options?.onInvalidate],
  )

  return {
    userStatus,
    counts,
    setStatus,
    isPending: pending,
    isSignedIn: Boolean(userId?.trim()),
    refreshSnapshot,
  }
}
